import type { ComponentType, ReactNode } from 'react';

import { cn } from '../lib/cn';

export type TableColumn<T> = {
  /** Identifiant stable de la colonne, sert de clé React. */
  key: string;
  /** En-tête. Absent = colonne sans libellé (actions, avatar…). */
  title?: string;
  /** Icône affichée avant le titre. */
  icon?: ComponentType<{ className?: string }>;
  /** Rendu d'une cellule. `index` est la position dans la page courante. */
  cell: (row: T, index: number) => ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
  headerClassName?: string;
  /** Absente des cartes mobiles (chevron de navigation, décor). */
  hideOnMobile?: boolean;
};

export type DataTableProps<T> = {
  data: T[];
  columns: TableColumn<T>[];
  /** Affiche un squelette à la place des lignes. */
  isLoading?: boolean;
  /** Nombre de lignes du squelette. */
  skeletonRows?: number;
  emptyMessage?: ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T, index: number) => string;
  /**
   * Identifiant stable d'une ligne. Fortement recommandé : sans lui, React
   * indexe par position et mélange l'état des lignes au moindre tri ou
   * suppression.
   */
  rowId?: (row: T) => string;
  className?: string;
  /**
   * Sous `md`, une carte par ligne à la place du tableau : la première colonne
   * en titre, les suivantes en paires « libellé : valeur ». Les colonnes sans
   * titre (actions) vont en pied de carte, sauf `hideOnMobile`. Un tableau de huit
   * colonnes défilant à l'horizontale se lit mal au pouce.
   */
  mobileCards?: boolean;
};

const ALIGN = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const;

/** Largeurs alternées : un squelette régulier a l'air d'un faux chargement. */
const SKELETON_WIDTHS = ['w-3/4', 'w-1/2', 'w-5/6'] as const;

/**
 * Tableau de données générique : colonnes déclaratives, état de chargement et
 * état vide intégrés. L'en-tête reste collé en haut au défilement, et le
 * tableau défile horizontalement sur petit écran plutôt que de déborder.
 */
export function DataTable<T>({
  data,
  columns,
  isLoading = false,
  skeletonRows = 5,
  emptyMessage = 'Aucun résultat.',
  onRowClick,
  rowClassName,
  rowId,
  className,
  mobileCards = false,
}: DataTableProps<T>) {
  const bodyCell = 'px-4 py-3 text-sm text-content';
  const [titre, ...autres] = columns;
  const details = autres.filter((col) => col.title);
  const actions = autres.filter((col) => !col.title && !col.hideOnMobile);

  return (
    <>
      {mobileCards && titre ? (
        <ul className="flex flex-col gap-3 md:hidden">
          {isLoading ? (
            Array.from({ length: Math.max(skeletonRows, 1) }).map((_, i) => (
              <li
                key={i}
                aria-hidden="true"
                className="h-28 animate-pulse rounded-card border border-border-subtle bg-surface"
              />
            ))
          ) : data.length === 0 ? (
            <li className="rounded-card border border-border-subtle bg-surface px-4 py-10 text-center text-sm text-content-muted">
              {emptyMessage}
            </li>
          ) : (
            data.map((row, index) => (
              <li
                key={rowId?.(row) ?? index}
                className={cn(
                  'flex flex-col gap-3 rounded-card border border-border-subtle bg-surface p-4 shadow-card',
                  onRowClick && 'cursor-pointer active:bg-surface-muted',
                  rowClassName?.(row, index),
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                <div className="min-w-0 text-sm text-content">{titre.cell(row, index)}</div>
                <dl className="flex flex-col gap-2 border-t border-border-subtle pt-3">
                  {details.map((col) => (
                    <div
                      key={col.key}
                      className="flex items-center justify-between gap-4 text-sm text-content"
                    >
                      <dt className="shrink-0 text-xs font-medium text-content-muted">
                        {col.title}
                      </dt>
                      <dd className="min-w-0 text-right">{col.cell(row, index)}</dd>
                    </div>
                  ))}
                </dl>
                {actions.length > 0 ? (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {actions.map((col) => (
                      <div key={col.key}>{col.cell(row, index)}</div>
                    ))}
                  </div>
                ) : null}
              </li>
            ))
          )}
        </ul>
      ) : null}

      <div
        className={cn(
          'overflow-x-auto rounded-card border border-border-subtle bg-surface shadow-card',
          mobileCards && titre && 'hidden md:block',
          className,
        )}
      >
        {/*
        `min-w-max` est ce qui rend le défilement possible : sans lui, `w-full`
        contraint la table à la largeur du conteneur et elle comprime ses
        colonnes jusqu'à les rendre illisibles, sans jamais déborder. La
        largeur effective devient ici `max(100 %, largeur naturelle)` — la
        table remplit un écran large, et déborde d'un écran étroit, ce que
        `overflow-x-auto` transforme alors en défilement.
      */}
        <table className="w-full min-w-max border-collapse">
          <thead className="sticky top-0 z-10 bg-surface-muted">
            <tr>
              {columns.map((col) => {
                const Icon = col.icon;

                return (
                  <th
                    key={col.key}
                    scope="col"
                    className={cn(
                      'border-b border-border-subtle px-4 py-3 text-xs font-semibold text-content-muted',
                      ALIGN[col.align ?? 'left'],
                      col.headerClassName,
                    )}
                  >
                    {Icon || col.title ? (
                      <span
                        className={cn(
                          'flex items-center gap-1.5',
                          col.align === 'center' && 'justify-center',
                          col.align === 'right' && 'justify-end',
                        )}
                      >
                        {Icon ? <Icon className="size-4 shrink-0" /> : null}
                        {col.title}
                      </span>
                    ) : (
                      // Colonne sans libellé : le lecteur d'écran a besoin d'un nom.
                      <span className="sr-only">{col.key}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-border-subtle">
            {isLoading ? (
              Array.from({ length: Math.max(skeletonRows, 1) }).map((_, rowIndex) => (
                <tr key={rowIndex} aria-hidden="true">
                  {columns.map((col, colIndex) => (
                    <td key={col.key} className={bodyCell}>
                      <div
                        className={cn(
                          'h-3 animate-pulse rounded bg-surface-muted',
                          SKELETON_WIDTHS[colIndex % SKELETON_WIDTHS.length],
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-sm text-content-muted"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={rowId?.(row) ?? index}
                  className={cn(
                    'transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-surface-muted',
                    rowClassName?.(row, index),
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(bodyCell, ALIGN[col.align ?? 'left'], col.className)}
                    >
                      {col.cell(row, index)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
